import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Competition } from "../types/Competition";
import { ShootingSession } from "../types/ShootingSession";
import { getShootingEventConfig } from "../configs/utils/ShootingEventConfigFactory";
import {
    getScoreOrderForEvent,
    buildScoreCounts,
    compareEntries,
    parsePointValue,
    RankedEntry,
} from "../components/EventRankingTable";

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

async function loadFont(pdf: jsPDF): Promise<void> {
    const [regularRes, mediumRes] = await Promise.all([
        fetch(`${process.env.PUBLIC_URL}/fonts/Roboto-Regular.ttf`),
        fetch(`${process.env.PUBLIC_URL}/fonts/Roboto-Medium.ttf`),
    ]);

    if (!regularRes.ok || !mediumRes.ok) {
        throw new Error("Nie udało się załadować czcionki do generowania PDF");
    }

    const [regularBuf, mediumBuf] = await Promise.all([
        regularRes.arrayBuffer(),
        mediumRes.arrayBuffer(),
    ]);

    pdf.addFileToVFS('Roboto-Regular.ttf', arrayBufferToBase64(regularBuf));
    pdf.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');

    pdf.addFileToVFS('Roboto-Medium.ttf', arrayBufferToBase64(mediumBuf));
    pdf.addFont('Roboto-Medium.ttf', 'Roboto', 'bold');
}

function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
}

export async function exportCompetitionReportToPdf(
    competition: Competition,
    fetchSessionsForEvent: (competitionId: string, shootingEventId: string) => Promise<ShootingSession[]>
): Promise<void> {
    if (competition.shootingEventIds.length === 0) return;

    const pdf = new jsPDF("p", "mm", "a4");
    await loadFont(pdf);
    pdf.setFont('Roboto');

    const margin = 15;

    const drawPageHeader = (eventName: string) => {
        pdf.setFont('Roboto', 'bold');
        pdf.setFontSize(14);
        pdf.text(competition.name, margin, 20);

        pdf.setFont('Roboto', 'normal');
        pdf.setFontSize(11);
        pdf.text(formatDate(competition.date), margin, 27);

        pdf.setFont('Roboto', 'bold');
        pdf.setFontSize(12);
        pdf.text(eventName, margin, 43);
    };

    const headerBottomY = 47;
    const pageTopMargin = headerBottomY;

    for (let i = 0; i < competition.shootingEventIds.length; i++) {
        const eventId = competition.shootingEventIds[i];
        const config = getShootingEventConfig(eventId);
        const sessions = await fetchSessionsForEvent(competition.id, eventId);
        const scoreOrder = getScoreOrderForEvent(eventId);

        if (i > 0) pdf.addPage();

        const eventName = config.shootingEventName;

        const entries: RankedEntry[] = sessions.map(session => {
            const allPoints = session.targetResults.flatMap(tr => tr.listOfPointsScored);
            const totalPoints = allPoints.reduce((sum, p) => sum + parsePointValue(p), 0);
            const scoreCounts = buildScoreCounts(session.targetResults, scoreOrder);
            return { session, totalPoints, scoreCounts, place: 0 };
        });

        entries.sort((a, b) => compareEntries(a, b, scoreOrder));

        let currentPlace = 1;
        entries.forEach((entry, index) => {
            if (index > 0 && compareEntries(entries[index - 1], entry, scoreOrder) !== 0) {
                currentPlace = index + 1;
            }
            entry.place = currentPlace;
        });

        const extraColumns = ["10X", "10/"].filter(s => scoreOrder.includes(s));
        const showPlain10 = extraColumns.length === 0 && scoreOrder.includes("10");

        const headers = ["M-ce", "Nazwisko i imię", "Klub", "Wynik"];
        extraColumns.forEach(col => headers.push(col === "10X" ? "10x" : "10"));
        if (showPlain10) headers.push("10");

        const rows = entries.map(entry => {
            const row: string[] = [
                entry.place.toString(),
                entry.session.owner,
                entry.session.club,
                entry.totalPoints.toString(),
            ];
            extraColumns.forEach(score => {
                row.push((entry.scoreCounts.get(score) ?? 0).toString());
            });
            if (showPlain10) {
                row.push((entry.scoreCounts.get("10") ?? 0).toString());
            }
            return row;
        });

        autoTable(pdf, {
            head: [headers],
            body: rows,
            startY: headerBottomY,
            margin: { left: margin, right: margin, top: pageTopMargin },
            styles: {
                font: 'Roboto',
                fontStyle: 'normal',
                fontSize: 10,
            },
            headStyles: {
                fillColor: [40, 167, 69],
                textColor: 255,
                fontStyle: 'bold',
            },
            alternateRowStyles: {
                fillColor: [232, 245, 233],
            },
            columnStyles: {
                0: { cellWidth: 12 },
                3: { cellWidth: 18 },
                ...(extraColumns.length >= 1 || showPlain10 ? { 4: { cellWidth: 12 } } : {}),
                ...(extraColumns.length >= 2 ? { 5: { cellWidth: 12 } } : {}),
            },
            didDrawPage: () => {
                drawPageHeader(eventName);
            },
        });
    }

    pdf.save(`${competition.name}.pdf`);
}
